import React from 'react';
import styled, { css } from 'styled-components';
import { colors } from 'shared/themes';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import RadioCheckedIcon from 'shared/assets/radio-checked';
import RadioUncheckedIcon from 'shared/assets/radio-unchecked';
import { TabValue } from 'src/checkout/components/sections/payment/shared/context';
import {
  Badge,
  BadgeContent,
  BadgeLabel,
  TabText,
  TabTextGroup,
} from 'src/checkout/components/sections/payment/selection/payment-tabs/payment-tabs.styles';
import { BASE_MAX_WIDTH, PLAID_OFFSET } from 'src/payments/constants';
import { PaymentBanner } from '../../shared/banner';

const EMBEDDED_MAX_WIDTH = PLAID_OFFSET + BASE_MAX_WIDTH;
const ERROR_COLOR = '#D34A4A';

export type TabOption = {
  value: TabValue;
  label: string;
  sublabel?: string;
  badgeLabel: string;
  badgeIcon?: React.ReactNode;
  hasError?: boolean;
};

type PaymentTabsProps = {
  children: React.ReactNode;
  onTabChange: (tab: string) => void;
  selectedTab: TabValue;
  tabOptions: TabOption[];
  showFeeBadgeInline?: boolean;
  showEmbeddedSearch?: boolean;
  alertMessage?: string;
};

const hasTabSelected = (selectedTab: TabValue): boolean => selectedTab !== TabValue.none;

const getTabBorder = (error: boolean, selected: boolean, accentColor: string): string => {
  if (error) {
    return `2px solid ${ERROR_COLOR}`;
  }
  if (selected) {
    return `2px solid ${accentColor}`;
  }
  return `1px solid ${String(colors.grey[85])}`;
};

const getTabContentBorder = (showEmbeddedSearch: boolean, error: boolean): string => {
  if (showEmbeddedSearch) {
    return 'none';
  }
  return error ? `1px solid ${ERROR_COLOR}` : `1px solid ${colors.grey[85]}`;
};

const RadioIndicator = ({ selected, color }: { selected: boolean; color?: string }): JSX.Element => (
  <RadioContainer>{selected ? <RadioCheckedIcon color={color} /> : <RadioUncheckedIcon />}</RadioContainer>
);

const FeeBadge = ({ badgeLabel, badgeIcon }: { badgeLabel: string; badgeIcon?: React.ReactNode }): JSX.Element => (
  <Badge>
    <BadgeContent>
      {!!badgeIcon && badgeIcon}
      <BadgeLabel>{badgeLabel}</BadgeLabel>
    </BadgeContent>
  </Badge>
);

export const PaymentTabs: React.FC<PaymentTabsProps> = ({
  selectedTab,
  onTabChange,
  children,
  tabOptions,
  showFeeBadgeInline = false,
  showEmbeddedSearch = false,
  alertMessage,
}) => {
  const theme = useStyledTheme();
  const accentColor: string = theme?.customized?.colors?.buttonsLinks ?? theme?.colors?.primaryBlue;
  const showNoSelectionError = !!alertMessage && !hasTabSelected(selectedTab);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = tabOptions.findIndex((tab) => tab.value === selectedTab);
    const totalTabs = tabOptions.length;

    const keyMap: Partial<Record<string, number>> = {
      arrowright: (currentIndex + 1) % totalTabs,
      arrowleft: (currentIndex - 1 + totalTabs) % totalTabs,
    };

    const newIndex = keyMap[e.key.toLowerCase()];
    if (newIndex !== undefined && newIndex !== currentIndex) {
      e.preventDefault();
      onTabChange(tabOptions[newIndex].value);
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const focusedTabIndex = tabOptions.findIndex((tab) => document.activeElement?.id === `tab-${tab.value}`);
      if (focusedTabIndex !== -1) {
        onTabChange(tabOptions[focusedTabIndex].value);
      }
    }
  };

  const firstTabValue = tabOptions[0]?.value ?? '';

  return (
    <TabsContainer data-testid='payment-tabs-v2' showEmbeddedSearch={showEmbeddedSearch}>
      <TabListSection>
        {!!alertMessage && (
          <AlertContainer isEmbeddedSearch={showEmbeddedSearch}>
            <PaymentBanner description={alertMessage} severity='errorV2' />
          </AlertContainer>
        )}
        <TabList role='tablist'>
          {tabOptions.map(({ value, label, sublabel, badgeLabel, badgeIcon, hasError: tabHasError }, index) => {
            const isSelected = selectedTab === value;
            const isFirstTab = index === 0;
            const isFocusable = isSelected || (!hasTabSelected(selectedTab) && isFirstTab);

            return (
              <Tab
                key={value}
                role='tab'
                id={`tab-${value}`}
                aria-controls={`tabpanel-${value}`}
                aria-selected={isSelected}
                tabIndex={isFocusable ? 0 : -1}
                selected={isSelected}
                error={showNoSelectionError || tabHasError}
                onClick={() => onTabChange(value)}
                onKeyDown={handleKeyDown}
                accentColor={accentColor}
              >
                <RadioIndicator selected={isSelected} color={accentColor} />
                <TabLabel>
                  <TabTextGroup gap={showFeeBadgeInline ? 2 : 1}>
                    <TabText size='medium' weight={isSelected ? 'semi' : 'regular'} selected={isSelected}>
                      {label}
                    </TabText>
                    {!!sublabel && (
                      <TabText size='medium' selected={isSelected}>
                        {sublabel}
                      </TabText>
                    )}
                    {!!badgeLabel && showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} badgeIcon={badgeIcon} />}
                  </TabTextGroup>
                  {!!badgeLabel && !showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} />}
                </TabLabel>
              </Tab>
            );
          })}
        </TabList>
      </TabListSection>

      {hasTabSelected(selectedTab) && (
        <TabContent
          role='tabpanel'
          id={`tabpanel-${selectedTab}`}
          aria-labelledby={`tab-${selectedTab}`}
          tabIndex={0}
          selectedTab={selectedTab}
          isFirst={selectedTab === firstTabValue}
          error={tabOptions.some((tab) => tab.hasError)}
          showEmbeddedSearch={showEmbeddedSearch}
        >
          {children}
        </TabContent>
      )}
    </TabsContainer>
  );
};

export const TabsContainer = styled.div<{ showEmbeddedSearch: boolean }>`
  display: flex;
  width: 100%;
  min-width: 320px;
  max-width: ${({ showEmbeddedSearch }) => (showEmbeddedSearch ? EMBEDDED_MAX_WIDTH : BASE_MAX_WIDTH)}px;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[3]};
`;

const TabListSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

export const TabList = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-self: stretch;
  max-width: ${BASE_MAX_WIDTH}px;
`;

export const Tab = styled.div<{ selected: boolean; error?: boolean; accentColor?: string }>`
  ${({ theme: { spaces, customized }, selected, error, accentColor = colors.primaryBlue }) => css`
    position: relative;
    display: flex;
    padding: ${spaces[3]};
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    align-content: space-between;
    flex: 1 0 0;
    height: 72px;
    border-radius: ${customized.radius.md};
    border: ${getTabBorder(!!error, selected, accentColor)};
    background: #fff;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${colors.primaryBlue};
      outline-offset: 2px;
    }
  `}
`;

export const TabLabel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;

const RadioContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

export const TabContent = styled.div<{ error?: boolean; showEmbeddedSearch?: boolean }>`
  ${({ theme: { customized }, error, showEmbeddedSearch }) => css`
    display: flex;
    min-height: 100px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: ${customized.radius.md};
    border: ${getTabContentBorder(!!showEmbeddedSearch, !!error)};
    background-color: ${showEmbeddedSearch ? 'transparent' : '#fff'};
    margin-left: ${showEmbeddedSearch ? `-${PLAID_OFFSET}px` : '0'};

    &:focus-visible {
      outline: 2px solid ${customized?.colors?.buttonsLinks ?? colors.primaryBlue};
      outline-offset: 2px;
    }
  `}
`;

const AlertContainer = styled.div<{ isEmbeddedSearch?: boolean }>`
  width: 100%;
  max-width: ${BASE_MAX_WIDTH}px;
  ${({ theme: { spaces }, isEmbeddedSearch }) =>
    isEmbeddedSearch &&
    css`
      padding: 0 ${String(spaces[1])};
      margin-top: ${String(spaces[1])};
    `}
`;
